import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Slide,
  alpha,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  Remove as MinimizeIcon,
  SmartToy as AssistantIcon,
} from '@mui/icons-material';
import { ASSISTANT_EMBED_URL } from '../constants/assistant';

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH_FRACTION = 2 / 3; // max 2/3 of viewport width
const STORAGE_KEY = 'assistant-panel-width';

function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const n = parseInt(stored, 10);
      const max = typeof window !== 'undefined' ? window.innerWidth * MAX_WIDTH_FRACTION : 800;
      if (!isNaN(n) && n >= MIN_WIDTH && n <= max) return n;
    }
  } catch {
    // ignore
  }
  return DEFAULT_WIDTH;
}

function storeWidth(w: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Math.round(w)));
  } catch {
    // ignore
  }
}

interface AssistantChatPanelProps {
  open: boolean;
  minimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onExpand: () => void;
}

export const AssistantChatPanel: React.FC<AssistantChatPanelProps> = ({
  open,
  minimized,
  onClose,
  onMinimize,
  onExpand,
}) => {
  const [panelWidth, setPanelWidth] = useState(getStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(panelWidth);
  widthRef.current = panelWidth;

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (e: MouseEvent) => {
      // Panel is right-aligned; dragging left = wider, dragging right = slimmer
      const newWidth = window.innerWidth - e.clientX;
      const maxW = window.innerWidth * MAX_WIDTH_FRACTION;
      const clamped = Math.max(MIN_WIDTH, Math.min(maxW, newWidth));
      widthRef.current = clamped;
      setPanelWidth(clamped);
    };

    const onUp = () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      setIsResizing(false);
      storeWidth(widthRef.current);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  // Closed: nothing to show
  if (!open) return null;

  // Minimized: floating FAB in bottom-right
  if (minimized) {
    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          borderRadius: 28,
          overflow: 'hidden',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
          },
        }}
        onClick={onExpand}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <AssistantIcon sx={{ fontSize: 24 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Need help? Ask the assistant
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Expanded: full panel with backdrop
  return (
    <>
      <Backdrop
        open={open}
        onClick={onMinimize}
        sx={{
          zIndex: 1299,
          bgcolor: 'rgba(0,0,0,0.2)',
          backdropFilter: 'blur(2px)',
        }}
      />
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={16}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: panelWidth,
          maxWidth: '66.67vw',
          height: '100vh',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Resize handle - left edge */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            zIndex: 1,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 3,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 2,
              height: 40,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.4)',
              opacity: 0,
              transition: 'opacity 0.15s',
            },
            '&:hover::after': {
              opacity: 1,
            },
          }}
          aria-label="Resize panel"
        />
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.95),
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssistantIcon sx={{ fontSize: 24 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Platform Assistant
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={onMinimize}
              sx={{ color: 'inherit', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              aria-label="Minimize"
            >
              <MinimizeIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'inherit', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* iframe */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            p: 0,
          }}
        >
          <iframe
            src={ASSISTANT_EMBED_URL}
            title="Platform Assistant"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
            }}
          />
        </Box>
      </Paper>
    </Slide>
    </>
  );
};
